/**
 * Static contract for the log analyzer agent's Run form: the default it opens
 * on, the limits it checks, and the field state `buildVariables` reads.
 *
 * Pinned against the agent's README and `log-filters.ts` in `ai-agents-hub`
 * (2026-10-06). The project, environment and source directory come from the
 * agent's own runtime, so the form sends only the log scope and the provider.
 */

import { DEFAULT_PROVIDER } from "../agentEngine/config";

import type { Provider } from "../agentEngine/config";
import type { FilterSelectValueMap } from "src/organization/pages/project/environment/metrics/logs/components/overview/FilterSelect/types";

/**
 * Task-type discriminator. `base: "log-analyzer-agent"` expands to a
 * `log-analyzer-agent:<major>:<build>` image type, so the prefix is stable
 * across the user-chosen task NAME (which can collide with a customer task).
 * Same prefix `agentReport.selectors` matches on for the Report tab.
 */
export const LOG_ANALYZER_AGENT_TASK_TYPE_PREFIX = "log-analyzer-agent:";

export const isLogAnalyzerAgentTask = (taskType: string | undefined): boolean =>
  Boolean(taskType?.startsWith(LOG_ANALYZER_AGENT_TASK_TYPE_PREFIX));

/** The Logs API's longest window. A project's retention can be shorter. */
export const MAX_WINDOW_SECONDS = 45 * 24 * 60 * 60;

/**
 * What the agent reads: the window and the filters shown on the Logs page when
 * the user opened the form. The opener passes it in; the form never reads the
 * page's URL.
 */
export interface LogScope {
  from: Date;
  to: Date;
  filters: FilterSelectValueMap;
}

/** The form's field state — the single shape `buildVariables`/`validate` read. */
export interface LogAnalyzerAgentFormState {
  /**
   * `MODEL`. The agent resolves the provider to its own model and picks the
   * thinking level, budget and findings cap itself.
   */
  provider: Provider;
}

export const initialLogAnalyzerAgentState = (): LogAnalyzerAgentFormState => ({
  provider: DEFAULT_PROVIDER
});
